import React from 'react';
import {
  emptyFilledFilters,
  ProcessRow,
  SearchStates,
  SelectOption
} from 'store/ducks/InternalControls/types';
import { Button, Input, Modal, notification, Tooltip } from 'antd';
import { DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import dayjs from 'dayjs';
import CustomSelect from 'components/CustomSelect';
import CustomDatePicker from 'components/CustomDatePicker';
import Helpers from 'utils/Helpers';
import * as Styled from '../pages/InternalControlsMain/RiskControl.styled';
import * as ControlStyled from '../pages/InternalControlsMain/InternalControls/InternalControls.styled';

const SearchableColumnTitle: React.FC<{
  title: string;
  searchText: string;
  setSearchText: (text: string) => void;
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
}> = ({ title, searchText, setSearchText, showSearch, setShowSearch }) => {
  return (
    <ControlStyled.SearchHeaderContainer>
      <span>{title}</span>
      <ControlStyled.ProcessSearchIcon
        onClick={() => setShowSearch(!showSearch)}
      />
      {showSearch && (
        <ControlStyled.WideSearchInput
          placeholder='Search'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          autoFocus
        />
      )}
    </ControlStyled.SearchHeaderContainer>
  );
};

export const getProcessColumns = (
  process_criticality: SelectOption[],
  processes: ProcessRow[],
  process_type: SelectOption[],
  group: SelectOption[],
  division: SelectOption[],
  region: SelectOption[],
  legal_entity: SelectOption[],
  business_unit: SelectOption[],
  business_process_parent: SelectOption[],
  setProcesses: (risks: ProcessRow[]) => void,
  showBusinessProcessParent: boolean,
  showOrgDimensions: boolean,
  parentFieldName?:
    | 'business_process_l1'
    | 'business_process_l2'
    | 'business_process_l3',
  parentTitle?: string,
  processLevel?: 'L1' | 'L2' | 'L3' | 'L4',
  newProcessIds?: Set<number>,
  searchStates?: SearchStates,
  t?: (key: string) => string
): ColumnsType<ProcessRow> => {
  const handleInputChange = (
    recordId: number,
    field: keyof ProcessRow,
    value: any
  ) => {
    const newProcesses = [...processes];
    const actualIndex = newProcesses.findIndex((p) => p.id === recordId);
    if (actualIndex !== -1) {
      newProcesses[actualIndex]._pinned = true;
      newProcesses[actualIndex][field] = value;
      setProcesses(newProcesses);
    }
  };

  const handleDateChange = (
    recordId: number,
    field: keyof ProcessRow,
    date: string | null
  ) => {
    const newProcesses = [...processes];
    const actualIndex = newProcesses.findIndex((p) => p.id === recordId);
    if (actualIndex !== -1) {
      newProcesses[actualIndex]._pinned = true;
      newProcesses[actualIndex][field] = date;
      setProcesses(newProcesses);
    }
  };

  const handleSelectChange = (
    recordId: number,
    field: keyof ProcessRow,
    id: number | undefined,
    options: SelectOption[]
  ) => {
    const newProcesses = [...processes];
    const actualIndex = newProcesses.findIndex((p) => p.id === recordId);
    if (actualIndex !== -1) {
      newProcesses[actualIndex]._pinned = true;
      if (id === undefined) {
        newProcesses[actualIndex][field] = null;
      } else {
        const selected = options.find((o) => o.value === id);
        newProcesses[actualIndex][field] = {
          id,
          name: selected?.label ?? null
        };
      }
      setProcesses(newProcesses);
    }
  };

  const handleDuplicateProcessRow = (record: ProcessRow) => {
    const newId = generateUniqueId();

    const duplicatedRow: ProcessRow = {
      ...structuredClone(record),
      id: newId,
      key: `${processes.length + 1}-${newId}`
    };

    const index = processes.findIndex((row) => row.id === record.id);
    const newProcesses = [...processes];
    newProcesses.splice(index + 1, 0, duplicatedRow);

    setProcesses(newProcesses);

    if (newProcessIds) {
      newProcessIds.add(newId);
    }
  };

  const columns: ColumnsType<ProcessRow> = [
    {
      title: () =>
        searchStates ? (
          <SearchableColumnTitle
            title={t ? t('PROCESS_TITLE') : 'Process title'}
            searchText={searchStates.title.search}
            setSearchText={searchStates.title.setSearch}
            showSearch={searchStates.title.showSearch}
            setShowSearch={searchStates.title.setShowSearch}
          />
        ) : t ? (
          t('PROCESS_TITLE')
        ) : (
          'Process title'
        ),
      dataIndex: 'title',
      key: 'title',
      width: searchStates?.title.showSearch ? 400 : 220,
      fixed: 'left',
      ellipsis: false,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('title'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={value || ''}
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value}
              placeholder='Enter process title'
              rows={2}
              onChange={(e) =>
                handleInputChange(record.id, 'title', e.target.value)
              }
            />
          </Tooltip>
        </>
      )
    },
    {
      title: () =>
        searchStates ? (
          <SearchableColumnTitle
            title={t ? t('DESCRIPTION') : 'Description'}
            searchText={searchStates.description.search}
            setSearchText={searchStates.description.setSearch}
            showSearch={searchStates.description.showSearch}
            setShowSearch={searchStates.description.setShowSearch}
          />
        ) : t ? (
          t('DESCRIPTION')
        ) : (
          'Description'
        ),
      dataIndex: 'description',
      key: 'description',
      width: searchStates?.description.showSearch ? 400 : 220,
      fixed: 'left',
      ellipsis: false,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('description'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={value || ''}
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value}
              placeholder='Enter process description'
              rows={3}
              onChange={(e) =>
                handleInputChange(record.id, 'description', e.target.value)
              }
            />
          </Tooltip>
        </>
      )
    },
    {
      title: (
        <Styled.ProcessType>
          <span>{t ? t('PROCESS_TYPE') : 'Process type'}</span>
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.ProcessType>
      ),
      dataIndex: 'process_type',
      key: 'process_type',
      width: 140,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('process_type'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={process_type}
          placeholder='Select process type'
          allowClear
          onChange={(id?: number) =>
            handleSelectChange(record.id, 'process_type', id, process_type)
          }
        />
      )
    },
    {
      title: (
        <>
          <style>
            {ControlStyled.getProcessValidityFilterStyle(processLevel)}
          </style>
          <ControlStyled.ProcessValidityHeaderWrapper className='process-validity-header'>
            <ControlStyled.ProcessHeaderWithSeparator>
              {t ? t('PROCESS_VALIDITY_FROM_TO') : 'Process validity from - to'}
              <ControlStyled.ProcessHeaderSeparatorLine />
            </ControlStyled.ProcessHeaderWithSeparator>
          </ControlStyled.ProcessValidityHeaderWrapper>
        </>
      ),
      key: 'process_validity',
      width: 300,
      filters: emptyFilledFilters,
      onFilter: (value: string | number | boolean, record: ProcessRow) => {
        if ((record as any)._pinned || (record as any).isNew) return true;
        const startFilled =
          record.start_date !== null &&
          record.start_date !== undefined &&
          record.start_date !== '';
        const endFilled =
          record.end_date !== null &&
          record.end_date !== undefined &&
          record.end_date !== '';
        const isFilled = startFilled || endFilled;
        return value === 'filled' ? isFilled : !isFilled;
      },
      onHeaderCell: () => ({
        style:
          processLevel === 'L4'
            ? ControlStyled.processValidityHeaderStyleL4
            : ControlStyled.processValidityHeaderStyle
      }),
      children: [
        {
          title: '',
          dataIndex: 'start_date',
          key: 'start_date',
          width: 140,
          onHeaderCell: () => ({
            style: ControlStyled.processDateHeaderNoBorder
          }),
          onCell: () => ({
            style: ControlStyled.startDateCellBorderStyle
          }),
          render: (value: string | null, record: ProcessRow) => (
            <CustomDatePicker
              value={value ? dayjs(value) : null}
              placeholder='Validity from'
              onChange={(date: dayjs.Dayjs | null) =>
                handleDateChange(
                  record.id,
                  'start_date',
                  date ? date.format('YYYY-MM-DD') : null
                )
              }
            />
          )
        },
        {
          title: '',
          dataIndex: 'end_date',
          key: 'end_date',
          width: 158,
          onHeaderCell: () => ({
            style: ControlStyled.processDateHeaderWithBorder
          }),
          onCell: () => ({
            style: ControlStyled.endDateCellPaddingStyle
          }),
          render: (value: string | null, record: ProcessRow) => (
            <CustomDatePicker
              value={
                value
                  ? dayjs(value === '' ? '9999-01-01' : value)
                  : dayjs('9999-01-01')
              }
              placeholder='Validity to'
              onChange={(date: dayjs.Dayjs | null) =>
                handleDateChange(
                  record.id,
                  'end_date',
                  date ? date.format('YYYY-MM-DD') : null
                )
              }
            />
          )
        }
      ]
    },
    {
      title: () =>
        searchStates ? (
          <SearchableColumnTitle
            title={t ? t('ACTIVATION_DATE') : 'Activation Date'}
            searchText={searchStates.activation.search}
            setSearchText={searchStates.activation.setSearch}
            showSearch={searchStates.activation.showSearch}
            setShowSearch={searchStates.activation.setShowSearch}
          />
        ) : t ? (
          t('ACTIVATION_DATE')
        ) : (
          'Activation Date'
        ),
      dataIndex: 'activation_date',
      key: 'activation_date',
      width: searchStates?.activation.showSearch
        ? processLevel === 'L4'
          ? 400
          : 320
        : processLevel === 'L4'
        ? 200
        : 180,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('activation_date'),
      render: (value: string | null, record: ProcessRow, index: number) => (
        <CustomDatePicker
          value={value ? dayjs(value) : null}
          onChange={(date: dayjs.Dayjs | null) =>
            handleDateChange(
              record.id,
              'activation_date',
              date ? date.format('YYYY-MM-DD') : null
            )
          }
        />
      )
    },
    {
      title: () =>
        searchStates ? (
          <SearchableColumnTitle
            title={t ? t('RESPONSIBLE_PERSON') : 'Responsible person'}
            searchText={searchStates.responsible.search}
            setSearchText={searchStates.responsible.setSearch}
            showSearch={searchStates.responsible.showSearch}
            setShowSearch={searchStates.responsible.setShowSearch}
          />
        ) : t ? (
          t('RESPONSIBLE_PERSON')
        ) : (
          'Responsible person'
        ),
      dataIndex: 'responsible_person',
      key: 'responsible_person',
      width: searchStates?.responsible.showSearch ? 400 : 220,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('responsible_person'),
      render: (value: string, record, index) => (
        <Input
          value={value}
          placeholder='Enter process responsible person'
          onChange={(e) =>
            handleInputChange(record.id, 'responsible_person', e.target.value)
          }
        />
      )
    },
    {
      title: () =>
        searchStates ? (
          <SearchableColumnTitle
            title={t ? t('COMMENTS') : 'Comments'}
            searchText={searchStates.comments.search}
            setSearchText={searchStates.comments.setSearch}
            showSearch={searchStates.comments.showSearch}
            setShowSearch={searchStates.comments.setShowSearch}
          />
        ) : t ? (
          t('COMMENTS')
        ) : (
          'Comments'
        ),
      dataIndex: 'comments',
      key: 'comments',
      width: searchStates?.comments.showSearch ? 400 : 220,
      ellipsis: false,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('comments'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={value || ''}
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value}
              placeholder='Enter comments'
              rows={2}
              onChange={(e) =>
                handleInputChange(record.id, 'comments', e.target.value)
              }
            />
          </Tooltip>
        </>
      )
    },
    {
      title: () =>
        searchStates ? (
          <SearchableColumnTitle
            title={t ? t('OWNER_MAINTAINER') : 'Owner maintainer'}
            searchText={searchStates.owner.search}
            setSearchText={searchStates.owner.setSearch}
            showSearch={searchStates.owner.showSearch}
            setShowSearch={searchStates.owner.setShowSearch}
          />
        ) : t ? (
          t('OWNER_MAINTAINER')
        ) : (
          'Owner maintainer'
        ),
      dataIndex: 'owner_maintainer',
      key: 'owner_maintainer',
      width: searchStates?.owner.showSearch ? 400 : 180,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('owner_maintainer'),
      render: (value: string, record, index) => (
        <Input
          value={value}
          placeholder='Enter owner maintainer'
          onChange={(e) =>
            handleInputChange(record.id, 'owner_maintainer', e.target.value)
          }
        />
      )
    }
  ];

  const actionColumn: ColumnType<ProcessRow> = {
    title: t ? t('ACTIONS') : 'Actions',
    key: 'actions',
    width: 100,
    fixed: 'left',
    render: (_, record) => (
      <>
        <Button
          type='link'
          danger
          icon={<DeleteOutlined />}
          onClick={() => {
            Modal.confirm({
              title: 'Are you sure?',
              content:
                'Do you want to delete this business process? This action cannot be undone.',
              okText: 'Yes, Delete',
              cancelText: 'Cancel',
              okType: 'danger',
              onOk: async () => {
                if (newProcessIds?.has(record.id)) {
                  // New record - just remove from local state without API call
                  const newProcesses = processes.filter((p) => p !== record);
                  setProcesses(newProcesses);
                  return;
                }

                try {
                  // SOFT DELETE COMMENTED OUT
                  // if (processLevel === 'L1') {
                  //   await ApiService.softDeleteBusinessProcessL1(record.id);
                  // } else if (processLevel === 'L2') {
                  //   await ApiService.softDeleteBusinessProcessL2(record.id);
                  // } else if (processLevel === 'L3') {
                  //   await ApiService.softDeleteBusinessProcessL3(record.id);
                  // } else {
                  //   await ApiService.softDeleteBusinessProcessL4(record.id);
                  // }
                  notification.success({
                    message: 'Business Process Deleted',
                    description:
                      'Business process has been soft deleted successfully.',
                    placement: 'topRight',
                    duration: 3
                  });
                  const newProcesses = processes.filter((p) => p !== record);
                  setProcesses(newProcesses);
                } catch (error) {
                  notification.error({
                    message: 'Delete Failed',
                    description:
                      'Failed to delete business process. Please try again.',
                    placement: 'topRight',
                    duration: 3
                  });
                }
              }
            });
          }}
        />
        <Styled.CopyIcon
          onClick={() => handleDuplicateProcessRow(record)}
          icon={<CopyOutlined />}
        />
      </>
    )
  };

  columns.unshift(actionColumn);

  if (showBusinessProcessParent && parentFieldName && parentTitle) {
    const parentColumn = {
      title: parentTitle,
      dataIndex: parentFieldName,
      key: parentFieldName,
      width: 180,
      render: (
        value: { id: number | null; name: string | null } | null,
        record: ProcessRow
      ) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={business_process_parent}
          placeholder={`Select ${parentTitle.toLowerCase()}`}
          allowClear
          showSearch
          onChange={(id?: number) =>
            handleSelectChange(
              record.id,
              parentFieldName,
              id,
              business_process_parent
            )
          }
        />
      )
    };

    // Add Process criticality column right after parent column
    const processCriticalityColumn = {
      title: 'Process criticality',
      dataIndex: 'process_criticality',
      key: 'process_criticality',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ProcessRow>('process_criticality'),
      width: 180,
      render: (value: any, record: ProcessRow, index: number) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={process_criticality}
          placeholder='Select process criticality'
          allowClear
          onChange={(id?: number) =>
            handleSelectChange(
              record.id,
              'process_criticality',
              id,
              process_criticality
            )
          }
        />
      )
    };

    const descriptionIndex = columns.findIndex(
      (col): col is ColumnType<ProcessRow> =>
        'dataIndex' in col && col.dataIndex === 'description'
    );

    if (descriptionIndex !== -1) {
      // Insert parent column, then process criticality column after description
      columns.splice(
        descriptionIndex + 1,
        0,
        parentColumn,
        processCriticalityColumn
      );
    } else {
      columns.push(parentColumn, processCriticalityColumn);
    }
  }

  // Move Owner/Maintainer column to be positioned after the parent process column
  // First, find and remove the existing Owner/Maintainer column
  const ownerColumnIndex = columns.findIndex(
    (col): col is ColumnType<ProcessRow> =>
      'dataIndex' in col && col.dataIndex === 'owner_maintainer'
  );

  let ownerColumn;
  if (ownerColumnIndex !== -1) {
    ownerColumn = columns.splice(ownerColumnIndex, 1)[0];
  }

  // If we have a parent column and owner column, insert owner column after parent and process criticality
  if (showBusinessProcessParent && ownerColumn) {
    const parentColumnIndex = columns.findIndex(
      (col): col is ColumnType<ProcessRow> =>
        'dataIndex' in col && col.dataIndex === parentFieldName
    );

    if (parentColumnIndex !== -1) {
      // Insert owner column after parent column + process criticality (so +2)
      columns.splice(parentColumnIndex + 2, 0, ownerColumn);
    } else {
      // Fallback: add at the end
      columns.push(ownerColumn);
    }
  } else if (ownerColumn) {
    // If no parent column, add owner column back to its original position
    columns.push(ownerColumn);
  }
  return columns;
};
